import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useTrackPaymentFlow } from '../trackingPaymentFlow'
import { UI_DEFAULTS } from '@/constants/UiPropertyKeys'

interface ExistingSubscriberMessageProps {
  benefits?: string[]
  productName?: string
}

export function ExistingSubscriberMessage({ benefits, productName }: ExistingSubscriberMessageProps) {
  // Use provided benefits or fall back to defaults
  const displayBenefits = benefits && benefits.length > 0 ? benefits : UI_DEFAULTS.REDEEM.BENEFITS
  const { t } = useTranslation()
  const track = useTrackPaymentFlow()

  useEffect(() => {
    track(TRACKING_TYPES.PAYMENT_COMPLETED_SUCCESS)
  }, [track])

  // Animated checkmark component
  const AnimatedCheckmark = () => (
    <div className="flex items-center justify-center">
      <svg
        className="w-16 h-16 text-green-500 animate-bounce"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          strokeWidth="2"
          className="opacity-25"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
          d="M9 12l2 2 4-4"
          className="animate-pulse"
        />
      </svg>
    </div>
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-[#05155E] from-15% via-[#0163D0] to-[#4B97FE] p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header with DUPR branding */}
          <div className="bg-gradient-to-tr from-[#05155E] from-15% via-[#0163D0] to-[#4B97FE] p-9">
            <p className="text-center text-white text-[40px] font-black tracking-[-4px]">DUPR</p>
          </div>

          {/* Main Content */}
          <div className="p-8">
            <div className="flex flex-col items-center gap-y-4 text-center">
              {/* Large Animated Checkmark */}
              <AnimatedCheckmark />

              {/* Success Message */}
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('subscription.alreadyActiveTitle', 'Great news!')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('subscription.alreadyActive', `You've already purchased ${productName ?? 'this product'}. You may now close this window.`)}
                </p>
              </div>

              {/* Benefits reminder */}
              <div className="w-full mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Your purchase includes:</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  {displayBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExistingSubscriberMessage